import { useCallback, useEffect, useState } from 'react';
import { Field, ProcessingRuleItem } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { useCurrentOrganization } from '@/modules/core/hooks';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { ProcessingRuleType } from '@/modules/HealthPlan/HealthPlan.types.ts';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores';

import { CARD_PURSE_LOCK } from '../ProcessingRuleModal.constants';
import { MainInfo } from '../ProcessingRuleModal.types';
import { buildTierTriggerIds, hasInvalidTriggers } from '../ProcessingRuleModal.utils';
import useCreateProcessingRuleQuery from '../queries/useCreateProcessingRule.query';

import useProcessingRuleMainFields from './useProcessingRuleMainFields';
import useTierFields, { AGGREGATE_TRIGGER_KEY, EMBEDDED_TRIGGER_KEY } from './useTierFields';
import { TierField } from './useTiers';

export interface Params {
  mainValue: MainInfo;
  tiers: TierField[];
  visible?: boolean;
  onClose?: (value: boolean) => void;
  updatedElement?: ProcessingRuleItem;
  onCreated?: (ruleId: string, ruleName: string) => void;
}
const NAME_ERROR = 'Details: ProcessingRule with name';
export default ({ mainValue, tiers, onClose, visible, updatedElement, onCreated }: Params) => {
  const { observingOrganization } = useCurrentOrganization();
  const linkedPlanIds = useHealthPlanNameStore((state) => state.state.plans);
  const isHidePurseLockEnabled = useFeatureFlag(FeatureFlags.HIDE_PURSE_LOCK);
  const [isProcessingRuleNameError, setProcessingRuleError] = useState(false);
  const [isServerError, setServerError] = useState(false);
  const { mutateAsync: handleSave, isLoading } = useCreateProcessingRuleQuery(updatedElement, onCreated);
  const [isSubmitted, setSubmited] = useState(false);

  const mainFields = useProcessingRuleMainFields();

  useEffect(() => {
    if (!visible) {
      setSubmited(false);
    }
  }, [visible]);
  const tierFields = useTierFields({});
  const handleCheckValidity = useCallback(async () => {
    const mainShape = mainFields.reduce(
      (result, item) => ({
        ...result,
        [item.name]: item.validator,
      }),
      {}
    ) as yup.ObjectShape;
    const mainSchema = yup.object({}).shape(mainShape);
    try {
      await mainSchema.validate(mainValue);
      await Promise.all(
        tiers.map(async (item) => {
          const allTierFields: Field[] = [];
          const getTierFieldValidators = (currentFields: Field[]) => {
            currentFields.forEach((field) => {
              if (field.isAbsent) {
                return;
              }
              allTierFields.push(field);

              if (field.subFields) {
                const fieldValue = _.get(item, field.name, false);
                if (fieldValue) {
                  const activeSubFields = field.subFields.filter(
                    (subField) => subField.parentValue === undefined || subField.parentValue === fieldValue
                  );
                  getTierFieldValidators(activeSubFields);
                }
              }
            });
          };
          getTierFieldValidators(tierFields);

          const tiersShape = allTierFields.reduce((result, currentField) => {
            if (currentField.name === AGGREGATE_TRIGGER_KEY || currentField.name === EMBEDDED_TRIGGER_KEY) {
              return result;
            }
            return {
              ...result,
              [currentField.name]: currentField.validator,
            };
          }, {}) as yup.ObjectShape;

          const tiersSchema = yup.object({}).shape(tiersShape);
          await tiersSchema.validate(item);
        }, {})
      );
      return true;
    } catch {
      return false;
    }
  }, [mainFields, mainValue, tierFields, tiers]);

  const handleSubmit = useCallback(async () => {
    setSubmited(true);
    const isValid = await handleCheckValidity();

    if (isValid && !hasInvalidTriggers(tiers, mainValue.processingRuleType)) {
      try {
        await handleSave({
          name: mainValue.name,
          organization_id: observingOrganization.id,
          processing_rule_type: mainValue.processingRuleType,
          tiers: tiers.map((item, index) => {
            const isPurseLockEnabled =
              mainValue.processingRuleType !== ProcessingRuleType.EMBEDDED_ONLY &&
              item.lockCardPurse === CARD_PURSE_LOCK;
            const planIdsForCardPurseLock =
              isPurseLockEnabled && item.planIdsForCardPurseLock?.length
                ? item.planIdsForCardPurseLock
                    .flat()
                    .filter((id) => id && (isHidePurseLockEnabled || linkedPlanIds?.includes(id)))
                    .map(Number)
                : [];

            return {
              name: item.name,
              payout_definition_id: item.payoutDefinition,
              tier_trigger_ids: buildTierTriggerIds(item, index, tiers.length, mainValue.processingRuleType),
              is_send_trigger_notification: item.isSendTriggerNotification,
              is_lock_purse: isPurseLockEnabled,
              plan_ids_for_card_purse_lock: planIdsForCardPurseLock,
              ...(item.nameToDisplay ? { display_name: item.nameToDisplay } : {}),
              ...(item.planId ? { plan_id: item.planId } : {}),
            };
          }),
        });
        if (onClose) onClose(false);
      } catch (e) {
        const message = _.get(e, 'response.data.elevate_error_message', '') as string;
        if (message.includes(NAME_ERROR)) {
          setProcessingRuleError(true);
        }
        if (e) setServerError(true);
      }
    }
  }, [
    handleCheckValidity,
    handleSave,
    linkedPlanIds,
    isHidePurseLockEnabled,
    mainValue.name,
    mainValue.processingRuleType,
    observingOrganization.id,
    onClose,
    tiers,
  ]);

  return {
    isProcessingRuleNameError,
    isServerError,
    isSubmitted,
    isLoading,
    handleSubmit,
    setServerError,
  };
};
